import { Money, MoneyInput } from '@rematter/frontend-lib-api-client';

/**
 * Returns a zero Money value with USD currency
 */
export function zeroMoney(): Money {
  return {
    amount: 0,
    currencyCode: 'USD' as const,
    __typename: 'Money' as const,
  };
}

/**
 * Returns a zero MoneyInput value with USD currency (for API inputs)
 */
export function zeroMoneyInput(): MoneyInput {
  return {
    amount: 0,
    currencyCode: 'USD' as const,
  };
}

/**
 * Adds Money values together (for display/form state)
 */
export function addMoney(a: Money, b: Money): Money {
  if (a.currencyCode !== b.currencyCode) {
    throw new Error(
      `Cannot add different currencies: ${a.currencyCode} and ${b.currencyCode}`,
    );
  }
  return {
    amount: a.amount + b.amount,
    currencyCode: a.currencyCode,
    __typename: 'Money' as const,
  };
}

/**
 * Converts a Money object to MoneyInput (removes __typename for API calls)
 */
export function toMoneyInput(
  money: Pick<Money, 'amount' | 'currencyCode'> | null | undefined,
): MoneyInput {
  if (!money) return zeroMoneyInput();

  // Explicitly destructure to exclude __typename and any other Apollo cache properties
  const { amount, currencyCode } = money;

  return {
    amount,
    currencyCode,
  };
}

/**
 * Converts a partial Money object to MoneyInput (removes __typename for API calls)
 */
export function toMoneyInputFromPartial(
  money: Partial<Money> | null | undefined,
): MoneyInput {
  if (
    !money ||
    money.amount === undefined ||
    money.currencyCode === undefined
  ) {
    return zeroMoneyInput();
  }

  // Explicitly destructure to exclude __typename and any other Apollo cache properties
  const { amount, currencyCode } = money;

  return {
    amount,
    currencyCode,
  };
}

export function toMoney(money: MoneyInput): Money {
  return {
    amount: money.amount,
    currencyCode: money.currencyCode,
    __typename: 'Money' as const,
  };
}

/**
 * Calculates total price by multiplying unit price by quantity
 * Returns a Money object with the calculated total
 */
export function calculateTotalPrice(
  unitPrice: Money | null | undefined,
  quantity: number | null | undefined,
): Money {
  // Handle edge cases
  if (!unitPrice || !quantity || quantity <= 0) {
    return zeroMoney();
  }

  return {
    amount: unitPrice.amount * quantity,
    currencyCode: unitPrice.currencyCode,
    __typename: 'Money' as const,
  };
}
